import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { AuthProvider, useAuth } from "./auth";
import { type NotifyCredentialSource, NotifyProvider } from "./core/notify";
import { PushProvider } from "./core/push";
import type { ShellClient } from "./core/shell";
import { createShellClient, ShellProvider } from "./core/shell";
import { AppShell } from "./mail/AppShell";
import { useViewport } from "./mail/useViewport";
import { applyUpdate, onUpdateAvailable, type PwaUpdate } from "./pwa";

// Offer the just-accepted credential to the browser's password manager
// (Credential Management API, Chromium). Safari/Firefox rely on the form's
// autocomplete tokens instead; the call is a no-op where PasswordCredential
// does not exist. Nothing is stored by Lately here — the vault holds the
// secret; this only makes AutoFill offer it on the next surface (Docked app,
// installed PWA, native app via the shared iCloud Keychain / Google PM entry).
function offerToPasswordManager(id: string, password: string) {
  const ctor = (
    window as unknown as {
      PasswordCredential?: new (data: {
        id: string;
        password: string;
        name?: string;
      }) => Credential;
    }
  ).PasswordCredential;
  if (!ctor || !navigator.credentials || typeof navigator.credentials.store !== "function") {
    return;
  }
  try {
    void navigator.credentials.store(new ctor({ id, password, name: "Lately" })).catch(() => {});
  } catch {
    // unsupported shape; ignore
  }
}

// The vault needs the passkey (or cannot be opened at all): shown before the
// mail UI. The ceremony must start from a click, so it lives behind a button.
function UnlockScreen() {
  const { vault, unlockVault, resetVault } = useAuth();
  const [resetting, setResetting] = useState(false);
  if (vault.kind !== "locked") {
    return null;
  }
  return (
    <main className="flex min-h-full items-center justify-center bg-background text-foreground">
      <div className="w-80 rounded-xl border border-border p-6 shadow-sm">
        <h1 className="mb-1 font-display text-2xl italic tracking-tight">Lately</h1>
        {vault.unlockable ? (
          <>
            <p className="mb-4 text-sm text-muted-foreground">
              Your mail is locked. Unlock it with your passkey (Touch ID / Face ID).
            </p>
            <button
              type="button"
              disabled={vault.busy}
              onClick={() => void unlockVault().catch(() => {})}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {vault.busy ? "Waiting for your passkey…" : "Unlock with passkey"}
            </button>
          </>
        ) : (
          <p className="mb-4 text-sm text-muted-foreground">
            This browser no longer holds the key to your local mail cache
            {vault.error ? ` (${vault.error})` : ""}. Sign in again — everything is re-synced from
            the server.
          </p>
        )}
        {vault.unlockable && vault.error ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {vault.error}
          </p>
        ) : null}
        <button
          type="button"
          disabled={resetting || vault.busy}
          onClick={() => {
            setResetting(true);
            void resetVault().finally(() => setResetting(false));
          }}
          className={`mt-2 w-full rounded-md px-4 py-2 text-sm transition-colors disabled:opacity-50 ${
            vault.unlockable
              ? "text-muted-foreground hover:bg-accent hover:text-foreground"
              : "bg-primary font-medium text-primary-foreground hover:bg-primary/90"
          }`}
        >
          {resetting ? "Clearing…" : vault.unlockable ? "Sign in again instead" : "Sign in again"}
        </button>
      </div>
    </main>
  );
}

function LoginScreen({ onCancel }: { onCancel?: () => void }) {
  const { signInEmail, signInBasic, connecting, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [server, setServer] = useState(import.meta.env.VITE_LATELY_JMAP_ORIGIN ?? "");
  const [username, setUsername] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  const fieldClass =
    "w-full rounded-md border border-input bg-background px-3 py-1.5 outline-none focus:ring-2 focus:ring-ring";

  return (
    <main className="flex min-h-full items-center justify-center bg-background text-foreground">
      <form
        className="w-80 rounded-xl border border-border p-6 shadow-sm"
        onSubmit={(event) => {
          event.preventDefault();
          const user = advanced && server.trim() ? username.trim() || email.trim() : email.trim();
          const attempt =
            advanced && server.trim()
              ? signInBasic(server, user, password)
              : signInEmail(email.trim(), needsPassword ? password : undefined);
          void attempt
            .then((result) => {
              if (result === "passwordRequired") setNeedsPassword(true);
              else if (password) offerToPasswordManager(user, password);
            })
            .catch(() => {});
        }}
      >
        <h1 className="mb-1 font-display text-2xl italic tracking-tight">Lately</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Sign in with your email — Lately finds your JMAP server.
        </p>
        <label className="mb-2 block text-sm">
          <span className="mb-1 block text-muted-foreground">Email</span>
          <input
            type="email"
            name="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setNeedsPassword(false);
            }}
            placeholder="you@example.com"
            autoComplete="username"
            required={!advanced}
            className={fieldClass}
          />
        </label>
        {needsPassword || advanced ? (
          <label className="mb-2 block text-sm">
            <span className="mb-1 block text-muted-foreground">Password</span>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              className={fieldClass}
            />
          </label>
        ) : null}
        <details
          className="mb-2"
          open={advanced}
          onToggle={(event) => setAdvanced(event.currentTarget.open)}
        >
          <summary className="cursor-pointer text-sm text-muted-foreground">Advanced</summary>
          <label className="mb-2 mt-2 block text-sm">
            <span className="mb-1 block text-muted-foreground">JMAP server</span>
            <input
              type="url"
              value={server}
              onChange={(event) => setServer(event.target.value)}
              placeholder="https://mail.example.com"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Username (if not your email)</span>
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              className={fieldClass}
            />
          </label>
        </details>
        {error ? (
          <p role="alert" className="mb-3 mt-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={connecting}
          className="mt-2 w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {connecting ? "Connecting…" : needsPassword || advanced ? "Sign in" : "Continue"}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 w-full rounded-md px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            Back to your inbox
          </button>
        ) : null}
      </form>
    </main>
  );
}

// Web Push lives with the signed-in state: the browser subscription is
// dropped when the last account is removed (the core already destroyed the
// server side in deleteAccount).
function PushGate({ children }: { children: ReactNode }) {
  const { shell, accounts } = useAuth();
  // What the notification record needs from the auth layer: each stored
  // account's credential (Basic today; a bearer once OIDC lands), read lazily
  // and only while "Notify me when Lately is closed" is on.
  const credentials = (): NotifyCredentialSource[] =>
    accounts.flatMap((account) => {
      const [scheme, value] = account.authorization.split(" ", 2);
      if (scheme?.toLowerCase() === "basic" && value) {
        const decoded = atob(value);
        const separator = decoded.indexOf(":");
        if (separator > 0) {
          return [
            {
              serverURL: account.origin,
              username: decoded.slice(0, separator),
              credential: { kind: "password" as const, secret: decoded.slice(separator + 1) },
            },
          ];
        }
      }
      if (scheme?.toLowerCase() === "bearer" && value) {
        return [
          {
            serverURL: account.origin,
            username: account.username,
            credential: { kind: "bearer" as const, secret: value },
          },
        ];
      }
      return [];
    });
  return (
    <PushProvider shell={shell} signedOut={accounts.length === 0}>
      <NotifyProvider shell={shell} credentials={credentials} signedOut={accounts.length === 0}>
        {children}
      </NotifyProvider>
    </PushProvider>
  );
}

function Gate() {
  const {
    active,
    session,
    accounts,
    activeIndex,
    addingAccount,
    setAddingAccount,
    switchAccount,
    removeAccount,
    vault,
  } = useAuth();

  if (vault.kind === "starting") {
    return (
      <main className="flex min-h-full items-center justify-center bg-background text-sm text-muted-foreground">
        Starting…
      </main>
    );
  }
  if (vault.kind === "locked") {
    return <UnlockScreen />;
  }
  if (addingAccount) {
    return <LoginScreen onCancel={() => setAddingAccount(false)} />;
  }
  if (active && session.status !== "idle") {
    // Offline-first: the shell streams the persisted state before (and even
    // if) the connect completes, so the mail UI mounts as soon as a session
    // is being opened and reflects progress through the snapshot.
    return (
      <AppShell
        key={`${active.origin}:${active.username}`}
        username={active.username}
        accounts={accounts}
        activeIndex={activeIndex}
        session={session}
        onSwitchAccount={switchAccount}
        onAddAccount={() => setAddingAccount(true)}
        onSignOut={() => removeAccount(activeIndex)}
      />
    );
  }
  if (active) {
    return (
      <main className="flex min-h-full items-center justify-center bg-background text-sm text-muted-foreground">
        Connecting…
      </main>
    );
  }
  return <LoginScreen />;
}

// PWA update flow: the service worker installed a newer shell behind the
// one this page runs on and is waiting; the user reloads when convenient
// (never mid-compose under them). "Reload" asks the worker to skip waiting;
// pwa.ts reloads on the resulting controllerchange. When another tab already
// activated the build, this tab is not yanked: the banner says so and Reload
// just reloads.
function UpdateBanner() {
  const [update, setUpdate] = useState<PwaUpdate | null>(null);
  const [reloading, setReloading] = useState(false);
  const { compact } = useViewport();
  useEffect(() => onUpdateAvailable(setUpdate), []);
  if (!update) {
    return null;
  }
  // Compact: clear the bottom bar (56px) and the home indicator; the pill is
  // fixed, so the body's safe-area padding does not apply to it.
  const bottom = compact
    ? "calc(3.5rem + env(safe-area-inset-bottom, 0px) + 0.75rem)"
    : "calc(env(safe-area-inset-bottom, 0px) + 1rem)";
  return (
    <output
      data-pwa-update={update.kind}
      style={{ bottom }}
      className="fixed left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground shadow-lg"
    >
      <span>{update.kind === "waiting" ? "Update available" : "Updated in another tab"}</span>
      <button
        type="button"
        disabled={reloading}
        onClick={() => {
          setReloading(true);
          if (!applyUpdate()) {
            setReloading(false);
          }
        }}
        className="rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
      >
        {reloading ? "Reloading…" : "Reload"}
      </button>
    </output>
  );
}

export default function App() {
  const [shell, setShell] = useState<ShellClient | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let client: ShellClient | null = null;
    let disposed = false;
    const abort = new AbortController();
    createShellClient(undefined, abort.signal)
      .then((created) => {
        if (disposed) {
          created.terminate();
          return;
        }
        client = created;
        setShell(created);
      })
      .catch((cause) => {
        if (!disposed) {
          setFailure(cause instanceof Error ? cause.message : String(cause));
        }
      });
    return () => {
      disposed = true;
      // Terminates a worker whose init is still pending, so a StrictMode
      // re-mount does not leave two workers contending for the OPFS pool.
      abort.abort();
      client?.terminate();
    };
  }, []);

  if (failure) {
    return (
      <main className="flex min-h-full items-center justify-center bg-background p-6 text-sm text-destructive">
        Lately could not start its mail engine: {failure}
      </main>
    );
  }
  if (!shell) {
    return (
      <main className="flex min-h-full items-center justify-center bg-background text-sm text-muted-foreground">
        Starting…
      </main>
    );
  }
  return (
    <ShellProvider client={shell}>
      <AuthProvider shell={shell}>
        <PushGate>
          <Gate />
        </PushGate>
      </AuthProvider>
      <UpdateBanner />
    </ShellProvider>
  );
}
